import {useState} from 'react'
import {Code2, Edit, CheckCircle2, AlertCircle, XCircle} from 'lucide-react'
import {Button} from '@/components/ui/button'
import {Badge} from '@/components/ui/badge'
import {Card, CardContent, CardDescription, CardTitle} from '@/components/ui/card'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {Switch} from '@/components/ui/switch'
import {Label} from '@/components/ui/label'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {FormField} from '@/components/shared/FormField'
import {FormGrid} from '@/components/shared/FormGrid'
import {FormSection} from '@/components/shared/FormSection'
import {SecretReferenceInput} from '@/components/shared/SecretReferenceInput'
import {RowCardHeader} from '@/components/shared/RowCardHeader'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {MutedText} from '@/components/shared/MutedText'
import {Strong} from '@/components/shared/Strong'
import {Icon} from '@/components/shared/Icon'
import {CodeText} from '@/components/shared/CodeText'
import {ErrorBanner} from '@/components/ui/error-banner'
import {InlineRow} from '@/components/shared/InlineRow'
import {Stack} from '@/components/shared/Stack'
import {Input} from '@/components/ui/input'
import {ScriptEditorCore} from '@/components/shared/ScriptEditorCore'
import {useI18n} from '@/i18n'
import {useAuth} from '@/contexts/auth-context'
import {permissions} from '@/lib/permissions'
import {useDialog} from '@/hooks/useDialog'
import {InboundAuthType} from '@/types/inbound-connector'
import type {
    ScriptInboundConfigCreateRequest,
    ScriptInboundConfigUpdateRequest,
    InboundScriptValidationResult,
} from '@/types/inbound-connector'
import {
    useScriptInboundConfig,
    useCreateScriptInboundConfig,
    useUpdateScriptInboundConfig,
    useValidateInboundScript,
} from '@/hooks/use-inbound-connectors'

// ─── Operations list (571.06: 4 boolean gates) ────────────────────────────────

type InboundOpKey = 'create' | 'update' | 'delete' | 'query'

const INBOUND_OPERATIONS: ReadonlyArray<{key: InboundOpKey; labelKey: string}> = [
    {key: 'create', labelKey: 'createOp'},
    {key: 'update', labelKey: 'updateOp'},
    {key: 'delete', labelKey: 'deleteOp'},
    {key: 'query', labelKey: 'queryOp'},
] as const

function buildDefaultEnabledOperations(): Record<InboundOpKey, boolean> {
    return {create: true, update: true, delete: true, query: true}
}

// ─── Default template ─────────────────────────────────────────────────────────

const DEFAULT_INBOUND_SCRIPT = `/*
 * Inbound connector script — one blob with all phase methods.
 *
 * Available bindings in every method:
 *   connector.http        — Pre-configured HTTP client (baseUrl + auth)
 *   log                   — SLF4J Logger
 *   ctx.connectorId       — Connector UUID
 *   ctx.syncType          — 'FULL' or 'PREVIEW'
 *   connector.secret(ref) — Resolve a secret by reference
 */

// ─── testConnection ───────────────────────────────────────────────────────────

def testConnection() {
    def start = System.currentTimeMillis()
    try {
        def response = connector.http.get("/api/health").execute()
        def duration = System.currentTimeMillis() - start
        if (response.statusCode in [200, 204]) {
            return [success: true, message: "Connected (\${duration}ms)"]
        }
        return [success: false, message: "HTTP \${response.statusCode} (\${duration}ms)"]
    } catch (Exception e) {
        def duration = System.currentTimeMillis() - start
        return [success: false, message: "\${e.message} (\${duration}ms)"]
    }
}

// ─── fullSync ─────────────────────────────────────────────────────────────────

def fullSync() {
    def allRecords = []
    def page = 0
    def pageSize = 100

    while (true) {
        def response = connector.http.get("/api/employees")
            .queryParam("page", page.toString())
            .queryParam("size", pageSize.toString())
            .asJson()

        if (response.statusCode != 200) {
            return [success: false, error: "HTTP \${response.statusCode}: \${response.body}"]
        }

        def items = response.body instanceof Map ? response.body.items : response.body

        items.each { item ->
            allRecords << [
                externalId: item.id?.toString(),
                entityType: 'IDENTITY',
                data: [
                    email      : item.email,
                    firstName  : item.firstName,
                    lastName   : item.lastName,
                    employeeNo : item.employeeNumber,
                    department : item.department
                ]
            ]
        }

        log.info("Fetched page {} — {} records so far", page, allRecords.size())
        if (items.size() < pageSize) break
        page++
    }

    log.info("Full sync complete: {} total records", allRecords.size())
    return [success: true, records: allRecords]
}

`

// ─── Types ───────────────────────────────────────────────────────────────────

interface ScriptFormState {
    authType: string
    tokenSecretRef: string
    tokenUrl: string
    clientId: string
    clientSecretRef: string
    scope: string
    username: string
    passwordSecretRef: string
    apiKeyHeader: string
    apiKeySecretRef: string
    baseUrl: string
    script: string
    enabledOperations: Record<InboundOpKey, boolean>
    connectTimeoutMs: number
    readTimeoutMs: number
    version: number
}

const DEFAULT_FORM: ScriptFormState = {
    authType: InboundAuthType.NONE,
    tokenSecretRef: '',
    tokenUrl: '',
    clientId: '',
    clientSecretRef: '',
    scope: '',
    username: '',
    passwordSecretRef: '',
    apiKeyHeader: '',
    apiKeySecretRef: '',
    baseUrl: '',
    script: DEFAULT_INBOUND_SCRIPT,
    enabledOperations: buildDefaultEnabledOperations(),
    connectTimeoutMs: 5000,
    readTimeoutMs: 30000,
    version: 0,
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface ScriptInboundConfigCardProps {
    connectorId: string
    canManage?: boolean
    canExecute?: boolean
}

// ─── Operation badge helpers ──────────────────────────────────────────────────

type BadgeKind = 'supported' | 'unsupported' | 'disabled'

interface OpBadgeState {
    key: InboundOpKey
    labelKey: string
    kind: BadgeKind
}

function computeOpBadges(
    enabledOperations: Record<InboundOpKey, boolean>,
    validationResult: InboundScriptValidationResult | null,
): OpBadgeState[] {
    return INBOUND_OPERATIONS.map(({key, labelKey}) => {
        const isEnabled = enabledOperations[key] !== false
        if (!isEnabled) {
            return {key, labelKey, kind: 'disabled'}
        }
        if (validationResult) {
            const supported = validationResult.supportedOperations ?? []
            const unsupported = validationResult.unsupportedOperations ?? []
            if (supported.includes(key)) {
                return {key, labelKey, kind: 'supported'}
            }
            if (unsupported.includes(key)) {
                return {key, labelKey, kind: 'unsupported'}
            }
        }
        return {key, labelKey, kind: 'supported'}
    })
}

function opBadgeVariant(kind: BadgeKind): 'success' | 'warning' | 'secondary' {
    if (kind === 'supported') return 'success'
    if (kind === 'unsupported') return 'warning'
    return 'secondary'
}

function OpBadgeIcon({kind}: {kind: BadgeKind}) {
    if (kind === 'supported') return <CheckCircle2 className="h-3 w-3 mr-1"/>
    if (kind === 'unsupported') return <AlertCircle className="h-3 w-3 mr-1"/>
    return <XCircle className="h-3 w-3 mr-1"/>
}

// ─── Validation banner ────────────────────────────────────────────────────────

function ValidationResultBanner({result, i18n}: {result: InboundScriptValidationResult; i18n: ReturnType<typeof useI18n>}) {
    if (result.valid) {
        return (
            <InlineRow gap="sm" className="p-3 rounded-md border border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950"> <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 shrink-0"/>
                <div>
                    <MutedText>{i18n.scriptConnector.validationSuccess}</MutedText>
                    {result.warnings.length > 0 && (
                        <ul className="mt-1 list-disc pl-4 text-sm text-amber-700 dark:text-amber-400">
                            {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
                        </ul>
                    )}
                </div>
            </InlineRow>
        )
    }
    const formattedErrors = result.errors
        .map((e) => (e.line > 0
            ? i18n.scriptConnector.errorAtLine({line: String(e.line), column: String(e.column), message: e.message})
            : e.message))
        .join('\n')
    return (
        <div className="p-3 rounded-md border border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950"> <InlineRow gap="sm">
                <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 shrink-0"/>
                <MutedText>{i18n.scriptConnector.validationError}</MutedText>
            </InlineRow>
            <ErrorBanner error={formattedErrors ? new Error(formattedErrors) : null}/>
        </div>
    )
}

// ─── Component ───────────────────────────────────────────────────────────────

export function ScriptInboundConfigCard({connectorId, canManage}: ScriptInboundConfigCardProps) {
    const i18n = useI18n()
    const {hasPermission} = useAuth()
    const canManageResolved = canManage ?? hasPermission(permissions.inboundConnectors.manage)

    const {data: config, isLoading, error, isError} = useScriptInboundConfig(connectorId)

    const createDialog = useDialog()
    const editDialog = useDialog()
    const [form, setForm] = useState<ScriptFormState>(DEFAULT_FORM)
    const [validationResult, setValidationResult] = useState<InboundScriptValidationResult | null>(null)
    const [validationNetworkError, setValidationNetworkError] = useState<Error | null>(null)

    const createMutation = useCreateScriptInboundConfig(connectorId)
    const updateMutation = useUpdateScriptInboundConfig(connectorId)
    const validateMutation = useValidateInboundScript(connectorId)

    const updateForm = (updates: Partial<ScriptFormState>) =>
        setForm((prev) => ({...prev, ...updates}))

    const openCreate = () => {
        setForm({...DEFAULT_FORM, enabledOperations: buildDefaultEnabledOperations()})
        setValidationResult(null)
        setValidationNetworkError(null)
        createDialog.open()
    }

    const openEdit = () => {
        if (!config) return
        setForm({
            authType: config.authType,
            tokenSecretRef: '',
            tokenUrl: config.tokenUrl ?? '',
            clientId: config.clientId ?? '',
            clientSecretRef: '',
            scope: config.scope ?? '',
            username: config.username ?? '',
            passwordSecretRef: '',
            apiKeyHeader: config.apiKeyHeader ?? '',
            apiKeySecretRef: '',
            baseUrl: config.baseUrl ?? '',
            script: config.script ?? DEFAULT_INBOUND_SCRIPT,
            enabledOperations: {
                create: config.createEnabled,
                update: config.updateEnabled,
                delete: config.deleteEnabled,
                query: config.queryEnabled,
            },
            connectTimeoutMs: config.connectTimeoutMs,
            readTimeoutMs: config.readTimeoutMs,
            version: config.version,
        })
        setValidationResult(null)
        setValidationNetworkError(null)
        editDialog.open()
    }

    /**
     * Run the connector-scoped validate endpoint and update local state.
     * Returns the structured result so save handlers can gate on `.valid`.
     */
    const runValidation = async (): Promise<InboundScriptValidationResult | null> => {
        setValidationNetworkError(null)
        try {
            const result = (await validateMutation.mutateAsync(form.script)) as InboundScriptValidationResult
            setValidationResult(result)
            return result
        } catch (err) {
            setValidationNetworkError(err as Error)
            setValidationResult(null)
            return null
        }
    }

    const handleCreate = async () => {
        const validation = await runValidation()
        if (!validation || !validation.valid) return
        const request: ScriptInboundConfigCreateRequest = {
            baseUrl: form.baseUrl || null,
            authType: form.authType || null,
            tokenSecretRef: form.tokenSecretRef || null,
            tokenUrl: form.tokenUrl || null,
            clientId: form.clientId || null,
            clientSecretRef: form.clientSecretRef || null,
            scope: form.scope || null,
            username: form.username || null,
            passwordSecretRef: form.passwordSecretRef || null,
            apiKeyHeader: form.apiKeyHeader || null,
            apiKeySecretRef: form.apiKeySecretRef || null,
            script: form.script || null,
            createEnabled: form.enabledOperations.create,
            updateEnabled: form.enabledOperations.update,
            deleteEnabled: form.enabledOperations.delete,
            queryEnabled: form.enabledOperations.query,
            connectTimeoutMs: form.connectTimeoutMs,
            readTimeoutMs: form.readTimeoutMs,
        }
        createMutation.mutate(request, {onSuccess: () => createDialog.close()})
    }

    const handleUpdate = async () => {
        const validation = await runValidation()
        if (!validation || !validation.valid) return
        const request: ScriptInboundConfigUpdateRequest = {
            version: form.version,
            baseUrl: form.baseUrl || null,
            authType: form.authType || null,
            tokenSecretRef: form.tokenSecretRef || null,
            tokenUrl: form.tokenUrl || null,
            clientId: form.clientId || null,
            clientSecretRef: form.clientSecretRef || null,
            scope: form.scope || null,
            username: form.username || null,
            passwordSecretRef: form.passwordSecretRef || null,
            apiKeyHeader: form.apiKeyHeader || null,
            apiKeySecretRef: form.apiKeySecretRef || null,
            script: form.script || null,
            createEnabled: form.enabledOperations.create,
            updateEnabled: form.enabledOperations.update,
            deleteEnabled: form.enabledOperations.delete,
            queryEnabled: form.enabledOperations.query,
            connectTimeoutMs: form.connectTimeoutMs,
            readTimeoutMs: form.readTimeoutMs,
        }
        updateMutation.mutate(request, {onSuccess: () => editDialog.close()})
    }

    const handleValidate = () => {
        void runValidation()
    }

    // WHY: exhaustive map indexed by every InboundAuthType key — TS will fail
    // the build when a new enum value is added without a label entry (issue
    // 928 — replaces a switch with `default: return type` silent fallback).
    // The `??` at the call site is the documented last-line fallback for the
    // forward-compat case where the backend speaks a newer enum value than
    // the deployed frontend knows about; humanise the raw code rather than
    // crashing the row.
    const AUTH_LABELS: Record<InboundAuthType, () => string> = {
        [InboundAuthType.BEARER_TOKEN]: () => i18n.inboundConnectors.authBearer,
        [InboundAuthType.OAUTH2_CLIENT]: () => i18n.inboundConnectors.authOAuth2,
        [InboundAuthType.OAUTH2_SAML_BEARER]: () => i18n.inboundConnectors.authOAuth2SamlBearer,
        [InboundAuthType.BASIC_AUTH]: () => i18n.inboundConnectors.authBasic,
        [InboundAuthType.API_KEY]: () => i18n.inboundConnectors.authApiKey,
        [InboundAuthType.MUTUAL_TLS]: () => i18n.inboundConnectors.authMutualTls,
        [InboundAuthType.AWS_SIGV4]: () => i18n.inboundConnectors.authAwsSigV4,
        // WHY: authSignedRequest / authNtlmKerberos / authGoogleSaJwt already exist
        // as labels in the inboundConnectors namespace (introduced for other connector
        // shapes); reuse them here instead of declaring duplicate keys.
        [InboundAuthType.SIGNED_REQUEST]: () => i18n.inboundConnectors.authSignedRequest,
        [InboundAuthType.NTLM_KERBEROS]: () => i18n.inboundConnectors.authNtlmKerberos,
        [InboundAuthType.GOOGLE_SA_JWT]: () => i18n.inboundConnectors.authGoogleSaJwt,
        [InboundAuthType.NONE]: () => i18n.inboundConnectors.authNone,
    }
    const getAuthLabel = (type: string): string =>
        AUTH_LABELS[type as InboundAuthType]?.() ?? type

    const getOperationLabel = (labelKey: string): string => {
        const key = labelKey as keyof typeof i18n.scriptConnector.operationLabels
        return i18n.scriptConnector.operationLabels[key] as string
    }

    // Loading state
    if (isLoading) {
        return (
            <Card data-testid="script-inbound-config-card-loading">
                <RowCardHeader>
                    <div>
                        <CardTitle data-testid="card-title-script-inbound-config-loading">{i18n.scriptConnector.title}</CardTitle>
                    </div>
                </RowCardHeader>
                <CardContent>
                    <MutedText>{i18n.common.loading}</MutedText>
                </CardContent>
            </Card>
        )
    }

    // Error (non-404)
    if (isError && (error as {status?: number})?.status !== 404) {
        return (
            <Card data-testid="script-inbound-config-card-error">
                <RowCardHeader>
                    <div>
                        <CardTitle data-testid="card-title-script-inbound-config-error">{i18n.scriptConnector.title}</CardTitle>
                    </div>
                </RowCardHeader>
                <CardContent>
                    <ErrorBanner error={error}/>
                </CardContent>
            </Card>
        )
    }

    const opBadges = config
        ? computeOpBadges(
            {
                create: config.createEnabled,
                update: config.updateEnabled,
                delete: config.deleteEnabled,
                query: config.queryEnabled,
            },
            null,
        )
        : []

    const formContent = (mode: 'create' | 'edit') => (
        <form
            onSubmit={(e) => {
                e.preventDefault()
                if (mode === 'create') { handleCreate() } else { handleUpdate() }
            }}
        >
            <div className="grid gap-6 py-4">
                {/* Authentication */}
                <FormSection title={i18n.scriptConnector.sectionAuthentication}>
                    <FormGrid columns={1}>
                        <FormField label={i18n.scriptConnector.baseUrl} htmlFor="inbound-script-baseUrl">
                            <Input
                                id="inbound-script-baseUrl"
                                data-testid="input-script-base-url"
                                value={form.baseUrl}
                                onChange={(e) => updateForm({baseUrl: e.target.value})}
                                placeholder={i18n.scriptConnector.baseUrlPlaceholder}
                            />
                        </FormField>
                        <FormField label={i18n.scriptConnector.authType} htmlFor="inbound-script-authType" required>
                            <Select
                                value={form.authType}
                                onValueChange={(v) => {
                                    updateForm({
                                        authType: v,
                                        tokenSecretRef: '',
                                        tokenUrl: '',
                                        clientId: '',
                                        clientSecretRef: '',
                                        scope: '',
                                        username: '',
                                        passwordSecretRef: '',
                                        apiKeyHeader: '',
                                        apiKeySecretRef: '',
                                    })
                                }}
                            >
                                <SelectTrigger id="inbound-script-authType" data-testid="select-script-auth-type">
                                    <SelectValue/>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={InboundAuthType.NONE}>{i18n.inboundConnectors.authNone}</SelectItem>
                                    <SelectItem value={InboundAuthType.BEARER_TOKEN}>{i18n.inboundConnectors.authBearer}</SelectItem>
                                    <SelectItem value={InboundAuthType.BASIC_AUTH}>{i18n.inboundConnectors.authBasic}</SelectItem>
                                    <SelectItem value={InboundAuthType.OAUTH2_CLIENT}>{i18n.inboundConnectors.authOAuth2}</SelectItem>
                                    <SelectItem value={InboundAuthType.API_KEY}>{i18n.inboundConnectors.authApiKey}</SelectItem>
                                </SelectContent>
                            </Select>
                        </FormField>

                        {form.authType === InboundAuthType.BEARER_TOKEN && (
                            <FormField label={i18n.inboundConnectors.tokenRef} htmlFor="inbound-script-tokenSecretRef">
                                <SecretReferenceInput
                                    id="inbound-script-tokenSecretRef"
                                    data-testid="input-script-token-secret-ref"
                                    value={form.tokenSecretRef}
                                    onChange={(v) => updateForm({tokenSecretRef: v})}
                                    placeholder={i18n.inboundConnectors.secretRefPlaceholder}
                                />
                            </FormField>
                        )}

                        {form.authType === InboundAuthType.OAUTH2_CLIENT && (<>
                            <FormField label={i18n.inboundConnectors.tokenUrl} htmlFor="inbound-script-tokenUrl">
                                <Input id="inbound-script-tokenUrl" value={form.tokenUrl} onChange={(e) => updateForm({tokenUrl: e.target.value})}/>
                            </FormField>
                            <FormField label={i18n.inboundConnectors.clientId} htmlFor="inbound-script-clientId">
                                <Input id="inbound-script-clientId" value={form.clientId} onChange={(e) => updateForm({clientId: e.target.value})}/>
                            </FormField>
                            <FormField label={i18n.inboundConnectors.clientSecretRef} htmlFor="inbound-script-clientSecretRef">
                                <SecretReferenceInput
                                    id="inbound-script-clientSecretRef"
                                    data-testid="input-script-client-secret-ref"
                                    value={form.clientSecretRef}
                                    onChange={(v) => updateForm({clientSecretRef: v})}
                                    placeholder={i18n.inboundConnectors.secretRefPlaceholder}
                                />
                            </FormField>
                            <FormField label={i18n.inboundConnectors.oauthScope} htmlFor="inbound-script-scope">
                                <Input id="inbound-script-scope" value={form.scope} onChange={(e) => updateForm({scope: e.target.value})}/>
                            </FormField>
                        </>)}

                        {form.authType === InboundAuthType.BASIC_AUTH && (<>
                            <FormField label={i18n.inboundConnectors.username} htmlFor="inbound-script-username">
                                <Input id="inbound-script-username" value={form.username} onChange={(e) => updateForm({username: e.target.value})}/>
                            </FormField>
                            <FormField label={i18n.inboundConnectors.passwordRef} htmlFor="inbound-script-passwordSecretRef">
                                <SecretReferenceInput
                                    id="inbound-script-passwordSecretRef"
                                    data-testid="input-script-password-secret-ref"
                                    value={form.passwordSecretRef}
                                    onChange={(v) => updateForm({passwordSecretRef: v})}
                                    placeholder={i18n.inboundConnectors.secretRefPlaceholder}
                                />
                            </FormField>
                        </>)}

                        {form.authType === InboundAuthType.API_KEY && (<>
                            <FormField label={i18n.inboundConnectors.apiKeyHeader} htmlFor="inbound-script-apiKeyHeader">
                                <Input
                                    id="inbound-script-apiKeyHeader"
                                    data-testid="input-script-api-key-header"
                                    value={form.apiKeyHeader}
                                    onChange={(e) => updateForm({apiKeyHeader: e.target.value})}
                                    placeholder={i18n.scriptConnector.apiKeyHeaderPlaceholder}
                                />
                            </FormField>
                            <FormField label={i18n.inboundConnectors.apiKeyRef} htmlFor="inbound-script-apiKeySecretRef">
                                <SecretReferenceInput
                                    id="inbound-script-apiKeySecretRef"
                                    data-testid="input-script-api-key-secret-ref"
                                    value={form.apiKeySecretRef}
                                    onChange={(v) => updateForm({apiKeySecretRef: v})}
                                    placeholder={i18n.inboundConnectors.secretRefPlaceholder}
                                />
                            </FormField>
                        </>)}
                    </FormGrid>
                </FormSection>

                {/* Timeouts */}
                <FormSection title={i18n.scriptConnector.sectionTimeouts}>
                    <FormGrid columns={2}>
                        <FormField label={i18n.scriptConnector.connectTimeoutMs} htmlFor="inbound-script-connectTimeout" required>
                            <Input
                                id="inbound-script-connectTimeout"
                                data-testid="input-script-connect-timeout"
                                type="number"
                                min={100}
                                max={60000}
                                value={form.connectTimeoutMs}
                                onChange={(e) => updateForm({connectTimeoutMs: parseInt(e.target.value, 10) || 5000})}
                            />
                        </FormField>
                        <FormField label={i18n.scriptConnector.readTimeoutMs} htmlFor="inbound-script-readTimeout" required>
                            <Input
                                id="inbound-script-readTimeout"
                                data-testid="input-script-read-timeout"
                                type="number"
                                min={100}
                                max={300000}
                                value={form.readTimeoutMs}
                                onChange={(e) => updateForm({readTimeoutMs: parseInt(e.target.value, 10) || 30000})}
                            />
                        </FormField>
                    </FormGrid>
                </FormSection>

                {/* Operations toggles */}
                <FormSection title={i18n.scriptConnector.sectionOperations}>
                    <div className="grid grid-cols-3 gap-3"> {INBOUND_OPERATIONS.map(({key, labelKey}) => (
                            <div key={key} className="flex items-center gap-2 py-1"> <Switch
                                    id={`inbound-op-toggle-${key}`}
                                    data-testid={`switch-op-${key.toLowerCase()}`}
                                    checked={form.enabledOperations[key] !== false}
                                    onCheckedChange={(checked) =>
                                        updateForm({
                                            enabledOperations: {...form.enabledOperations, [key]: checked},
                                        })
                                    }
                                />
                                <Label htmlFor={`inbound-op-toggle-${key}`} className="text-sm">
                                    {getOperationLabel(labelKey)}
                                </Label>
                            </div>
                        ))}
                    </div>
                </FormSection>

                {/* Single script editor */}
                <Stack>
                    <div className="flex items-center justify-between">
                        <Strong>{i18n.scriptConnector.sectionScript}</Strong>
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            data-testid="btn-validate-script"
                            onClick={handleValidate}
                            disabled={validateMutation.isPending}
                        >
                            {i18n.scriptConnector.validateScript}
                        </Button>
                    </div>
                    <MutedText size="sm">{i18n.scriptConnector.scriptDescription}</MutedText>

                    {/* Validation network error */}
                    {validationNetworkError && (
                        <ErrorBanner error={validationNetworkError}/>
                    )}

                    {/* Validation result */}
                    {validationResult && (
                        <ValidationResultBanner result={validationResult} i18n={i18n}/>
                    )}

                    {/* Operation badges (post-validate) */}
                    {validationResult && (
                        <div className="flex flex-wrap gap-2">
                            {INBOUND_OPERATIONS.map(({key, labelKey}) => {
                                const badges = computeOpBadges(form.enabledOperations, validationResult)
                                const badge = badges.find((b) => b.key === key)!
                                return (
                                    <Badge
                                        key={key}
                                        variant={opBadgeVariant(badge.kind)}
                                        data-testid={`badge-op-${key.toLowerCase()}`}
                                        className="flex items-center"
                                    >
                                        <OpBadgeIcon kind={badge.kind}/>
                                        {getOperationLabel(labelKey)}
                                    </Badge>
                                )
                            })}
                        </div>
                    )}

                    <div className="border rounded-md overflow-hidden min-h-[400px]"> <ScriptEditorCore
                            script={form.script}
                            onScriptChange={(s) => updateForm({script: s})}
                            hideSyntaxToolbar
                            externalSyntaxErrors={validationResult && !validationResult.valid ? validationResult.errors : null}
                        />
                    </div>
                </Stack>
            </div>

            <SaveCancelFooter
                testIdPrefix={mode === 'create' ? 'create-script' : 'edit-script'}
                onCancel={() =>
                    mode === 'create' ? createDialog.close() : editDialog.close()
                }
                saving={
                    mode === 'create' ? createMutation.isPending : updateMutation.isPending
                }
                saveLabel={
                    mode === 'create'
                        ? i18n.connectors.saveConfiguration
                        : i18n.common.saveChanges
                }
            />
        </form>
    )

    return (
        <>
            <Card
                data-testid="script-inbound-config-card"
            >
                <RowCardHeader>
                    <div>
                        <CardTitle className="items-center gap-2" data-testid="card-title-script-inbound-config">
                            <Code2 className="h-5 w-5"/>
                            {i18n.scriptConnector.title}
                        </CardTitle>
                        <CardDescription>{i18n.scriptConnector.description}</CardDescription>
                    </div>
                    <div className="flex gap-2">
                        {canManageResolved && (
                            config ? (
                                <Button
                                    data-testid="btn-edit-script-config"
                                    size="sm"
                                    onClick={openEdit}
                                >
                                    <Icon icon={Edit} className="mr-2"/>
                                    {i18n.common.edit}
                                </Button>
                            ) : (
                                <Button
                                    data-testid="btn-create-script-config"
                                    size="sm"
                                    onClick={openCreate}
                                >
                                    <Icon icon={Code2} className="mr-2"/>
                                    {i18n.scriptConnector.createConfig}
                                </Button>
                            )
                        )}
                    </div>
                </RowCardHeader>
                <CardContent>
                    {config ? (
                        <>
                            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                                <div className="sm:col-span-2">
                                    <dt className="font-medium text-muted-foreground">
                                        {i18n.scriptConnector.baseUrl}
                                    </dt>
                                    <dd>
                                        {config.baseUrl ? (
                                            <CodeText>{config.baseUrl}</CodeText>
                                        ) : (
                                            <MutedText size="sm">{i18n.scriptConnector.noBaseUrl}</MutedText>
                                        )}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="font-medium text-muted-foreground">
                                        {i18n.scriptConnector.authType}
                                    </dt>
                                    <dd>
                                        <CodeText>{getAuthLabel(config.authType)}</CodeText>
                                    </dd>
                                </div>
                                <div>
                                    <dt className="font-medium text-muted-foreground">
                                        {i18n.scriptConnector.operations}
                                    </dt>
                                    <dd className="mt-1 flex flex-wrap gap-1">
                                        {opBadges.map(({key, labelKey, kind}) => (
                                            <Badge
                                                key={key}
                                                variant={opBadgeVariant(kind)}
                                                data-testid={`badge-op-display-${key.toLowerCase()}`}
                                                className="flex items-center"
                                            >
                                                <OpBadgeIcon kind={kind}/>
                                                {getOperationLabel(labelKey)}
                                            </Badge>
                                        ))}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="font-medium text-muted-foreground">
                                        {i18n.scriptConnector.script}
                                    </dt>
                                    <dd className="mt-1">
                                        {config.script ? (
                                            <Badge variant="secondary">{i18n.scriptConnector.configured}</Badge>
                                        ) : (
                                            <MutedText size="sm">{i18n.scriptConnector.notConfigured}</MutedText>
                                        )}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="font-medium text-muted-foreground">
                                        {i18n.scriptConnector.connectTimeoutMs}
                                    </dt>
                                    <dd className="mt-1">
                                        {i18n.common.valueWithMs({value: config.connectTimeoutMs})}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="font-medium text-muted-foreground">
                                        {i18n.scriptConnector.readTimeoutMs}
                                    </dt>
                                    <dd className="mt-1">
                                        {i18n.common.valueWithMs({value: config.readTimeoutMs})}
                                    </dd>
                                </div>
                            </dl>

                        </>
                    ) : (
                        <MutedText block centered className="py-4">
                            {i18n.scriptConnector.noConfig}
                        </MutedText>
                    )}
                </CardContent>
            </Card>

            {/* Create dialog */}
            <Dialog open={createDialog.isOpen} onOpenChange={createDialog.bind.onOpenChange}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto"> <DialogHeader>
                        <DialogTitle>{i18n.scriptConnector.createDialogTitle}</DialogTitle>
                        <DialogDescription>{i18n.scriptConnector.createDialogDescription}</DialogDescription>
                    </DialogHeader>
                    {formContent('create')}
                </DialogContent>
            </Dialog>

            {/* Edit dialog */}
            <Dialog open={editDialog.isOpen} onOpenChange={editDialog.bind.onOpenChange}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto"> <DialogHeader>
                        <DialogTitle>{i18n.scriptConnector.editDialogTitle}</DialogTitle>
                        <DialogDescription>{i18n.scriptConnector.editDialogDescription}</DialogDescription>
                    </DialogHeader>
                    {formContent('edit')}
                </DialogContent>
            </Dialog>

        </>
    )
}

// Re-export for permission-checking
export function useScriptInboundPermissions() {
    const {hasPermission} = useAuth()
    const canManage = hasPermission(permissions.inboundConnectors.manage)
    const canExecute = hasPermission(permissions.inboundConnectors.execute)
    return {canManage, canExecute}
}
